import { useFocusRing } from '@react-aria/focus';
import { useTableCell } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { TableState } from '@react-stately/table';
import { GridNode } from '@react-types/grid';
import React from 'react';

import { useDOMRef } from './hooks/useDOMRef';
import { tableCellClassNames } from './styles';

type CellProps<T> = GridNode<T> & { rendered: React.ReactNode };

interface Props<T> {
    cell: CellProps<T>;
    state: TableState<T>;
    // @internal
    isStatic?: boolean;
}

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableCellProps<T = unknown> = Props<T> & NativeAttrs;

const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
    ({ cell, state, isStatic, ...props }, ref: React.Ref<HTMLTableCellElement | null>) => {
        const domRef = useDOMRef<HTMLTableCellElement>(ref);

        const {
            gridCellProps
        }: {
            gridCellProps: Omit<React.HTMLAttributes<unknown>, keyof TableCellProps<unknown>>;
        } = useTableCell({ node: cell }, state, domRef);

        // With infinite scroll the head and every row become their own `table-fixed` table, so a
        // width set on the column reaches the <th> but never the <td> below it — head and body then
        // lay out on two different grids. Carry the column's width down to the cell.
        const column = cell.column ?? (state.collection.columns as GridNode<unknown>[])?.[cell.index];
        const width = (column?.props as { width?: number | string })?.width;

        const { isFocusVisible, focusProps } = useFocusRing();

        const customGridCellProps = isStatic
            ? {
                  ...gridCellProps,
                  onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                  onPointerDown: (e: MouseEvent) => e.stopPropagation()
              }
            : gridCellProps;

        return (
            <td
                ref={domRef}
                className={tableCellClassNames({ className: props.className, isStatic, isFocusVisible })}
                {...mergeProps(customGridCellProps, focusProps, props)}
                style={width === undefined ? props.style : { ...props.style, width }}
            >
                {cell.rendered}
            </td>
        );
    }
);

TableCell.displayName = 'TableCell';

export default TableCell;
